/**
 * @voltxampere/engine/mcu — Intel HEX parser (Sprint 46-5)
 *
 * Parses Intel HEX format `.hex` text into a flat `Uint8Array` of
 * flash bytes ready for `loadFlashBytes()`. Sprint 46 MVP supports
 * the two record types every avr-gcc / Arduino toolchain emits:
 *
 *   00 — Data record:  `:LLAAAA00DD...DDCC`
 *   01 — End-of-file:  `:00000001FF`
 *
 * Out of scope (Sprint 47+):
 *
 *   02 — Extended segment address (16-bit segment offset, used by
 *        very old DOS-style HEX files; modern AVR-libc never emits)
 *   04 — Extended linear address (32-bit address, used by 4 MB+
 *        targets — ATmega2560 256 KB stays well under 64 KB so a
 *        plain 16-bit address suffices)
 *   05 — Start linear address (entry point hint, unused by AVR)
 *
 * Reference: Intel HEX format specification (Intel Application Note
 * AP-66) + AVR-libc avr/binutils objcopy `-O ihex` output.
 *
 * Format breakdown of one record line:
 *
 *   :LLAAAATT[DD...]CC
 *
 *   :       — record start marker (one ASCII colon)
 *   LL      — byte count (1 hex byte)
 *   AAAA    — 16-bit big-endian address (2 hex bytes)
 *   TT      — record type (1 hex byte; 00 = data, 01 = EOF)
 *   DD...   — LL data bytes
 *   CC      — two's-complement checksum of all preceding bytes
 *             (1 hex byte; sum-mod-256-then-negate)
 *
 * The parser validates the checksum on every record and throws on
 * mismatch (caller catches and surfaces "corrupt .hex" to user).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Parser error — thrown on malformed input or checksum mismatch. */
export class IntelHexParseError extends Error {
    line;
    constructor(message, line) {
        super(`Intel HEX line ${String(line)}: ${message}`);
        this.line = line;
        this.name = "IntelHexParseError";
    }
}
/**
 * Convert a 2-character hex substring to its byte value. Throws if
 * either character is not a valid hex digit.
 */
function hexByte(text, offset, line) {
    if (offset + 2 > text.length) {
        throw new IntelHexParseError(`expected hex byte at offset ${String(offset)}`, line);
    }
    const slice = text.substring(offset, offset + 2);
    const value = parseInt(slice, 16);
    if (Number.isNaN(value)) {
        throw new IntelHexParseError(`invalid hex byte '${slice}'`, line);
    }
    return value;
}
/**
 * Parse a single Intel HEX line. Returns the data bytes + 16-bit
 * address for type-00 records, returns null for type-01 (EOF), and
 * throws for unsupported types or checksum failures.
 */
function parseRecord(line, lineNumber) {
    if (!line.startsWith(":")) {
        throw new IntelHexParseError("missing ':' record start", lineNumber);
    }
    // Drop trailing CR/LF/whitespace.
    const body = line.replace(/[\r\n\s]+$/u, "");
    if (body.length < 11) {
        // Minimum: ':' + LL + AAAA + TT + CC = 1 + 2 + 4 + 2 + 2 = 11 chars
        throw new IntelHexParseError(`record too short (${String(body.length)} chars)`, lineNumber);
    }
    const byteCount = hexByte(body, 1, lineNumber);
    const expectedLength = 11 + byteCount * 2;
    if (body.length !== expectedLength) {
        throw new IntelHexParseError(`length mismatch: byteCount=${String(byteCount)} expects ${String(expectedLength)} chars, got ${String(body.length)}`, lineNumber);
    }
    const addrHi = hexByte(body, 3, lineNumber);
    const addrLo = hexByte(body, 5, lineNumber);
    const address = (addrHi << 8) | addrLo;
    const recordType = hexByte(body, 7, lineNumber);
    // Compute checksum over byteCount + addr_hi + addr_lo + type + data.
    let sum = byteCount + addrHi + addrLo + recordType;
    const data = new Uint8Array(byteCount);
    for (let i = 0; i < byteCount; i += 1) {
        const byte = hexByte(body, 9 + i * 2, lineNumber);
        data[i] = byte;
        sum += byte;
    }
    const expectedChecksum = -sum & 0xff;
    const actualChecksum = hexByte(body, 9 + byteCount * 2, lineNumber);
    if (expectedChecksum !== actualChecksum) {
        throw new IntelHexParseError(`checksum mismatch: expected 0x${expectedChecksum.toString(16).padStart(2, "0")}, got 0x${actualChecksum.toString(16).padStart(2, "0")}`, lineNumber);
    }
    if (recordType === 0x00) {
        return { address, data };
    }
    if (recordType === 0x01) {
        return null; // EOF
    }
    // Sprint 46 MVP: 02/03/04/05 not supported.
    throw new IntelHexParseError(`unsupported record type 0x${recordType.toString(16).padStart(2, "0")} (Sprint 46 MVP supports 00 + 01 only)`, lineNumber);
}
/**
 * Parse a complete Intel HEX text into a flash byte image.
 *
 * The parser:
 *   1. Splits input on newlines (CRLF / LF / mixed).
 *   2. Skips blank lines.
 *   3. Validates each record's checksum.
 *   4. Writes data bytes into the output array at their declared
 *      address (gaps stay as zero — matches avr-libc's
 *      `__do_clear_bss` runtime-zero assumption).
 *   5. Stops at the first type-01 EOF record.
 *
 * Throws `IntelHexParseError` on any malformation. The caller
 * (Sprint 47-1 Inspector runtime panel `.hex` upload handler) catches
 * the error and surfaces a toast / error message.
 */
export function parseIntelHex(hexText) {
    // Pre-allocate generously and shrink at the end. 64 KB matches the
    // ATmega2560 (256 KB physical, but flat HEX can address 64 KB at a
    // time without type-04 ext-linear records — Sprint 47+ scope).
    const buffer = new Uint8Array(64 * 1024);
    let maxAddress = 0;
    let recordCount = 0;
    let sawEOF = false;
    const lines = hexText.split(/\r\n|\r|\n/u);
    for (let i = 0; i < lines.length; i += 1) {
        const rawLine = lines[i] ?? "";
        const trimmed = rawLine.trim();
        if (trimmed === "")
            continue;
        const record = parseRecord(trimmed, i + 1);
        if (record === null) {
            sawEOF = true;
            break;
        }
        for (let j = 0; j < record.data.length; j += 1) {
            const addr = record.address + j;
            if (addr >= buffer.length) {
                throw new IntelHexParseError(`address 0x${addr.toString(16)} exceeds Sprint 46 MVP 64 KB limit`, i + 1);
            }
            buffer[addr] = record.data[j] ?? 0;
            if (addr + 1 > maxAddress)
                maxAddress = addr + 1;
        }
        recordCount += 1;
    }
    if (!sawEOF) {
        throw new IntelHexParseError("missing EOF record (type 01)", lines.length);
    }
    // Slice down to the actual flash size used.
    return {
        bytes: buffer.slice(0, maxAddress),
        maxAddress,
        recordCount,
    };
}
//# sourceMappingURL=hex-parser.js.map